/**
 * The handoff (D170): how a page on a creator's own domain comes to know
 * who the player is.
 *
 * @remarks
 * The identity cookie is `Domain=orpheusquest.com`, and a cookie is per
 * site: from `play.theirgame.com` a browser will not send it, whatever we
 * do. platform.md §5 names the fix — the page gets a short-lived token from
 * the home site — and this module is its pure half: the questions and the
 * addresses, with no browser in them, so every branch is a unit test.
 *
 * The journey, from a FOREIGN hostname (one not under the identity's
 * site): Sign in sends the player to the home host with the return
 * address in the query (`?handoff=<return>`). The home host — where the
 * cookie IS first-party — signs them in if it must, checks the return
 * hostname against the host records (D162's `GET /hosts/:hostname`, so a
 * token can only ever go to a hostname we serve), mints a one-time token,
 * and sends them back with it in the FRAGMENT (`#handoff=<token>`) — never
 * the query, so it reaches no server log and no `Referer`. The foreign
 * page strips it before anything else, exchanges it once for a bearer
 * session, and keeps that in `localStorage` (Mike's ruling, 2026-09-11):
 * one redirect per device, not per visit. From then on every credentialed
 * request carries `Authorization: Bearer …` where a home page's carries
 * the cookie.
 *
 * Mike's other ruling: the handoff happens when the player presses Sign
 * in — never on load. Anonymous play on a creator's domain bounces nowhere.
 *
 * @packageDocumentation
 */

import { HANDOFF_RETURN_PARAM, HANDOFF_TOKEN_PARAM } from '@orpheus/api-contracts';

/** Where the bearer session is kept on a foreign hostname. */
export const BEARER_SESSION_STORAGE_KEY = 'orpheus.bearer-session';

/**
 * Whether a hostname is foreign to the identity's site — neither the site
 * itself nor under it. Local development (`localhost`, a loopback address)
 * is never foreign: the cookie there is host-only and works as it always
 * has. Without a site (a build with no identity), nothing is foreign.
 *
 * @param hostname - The page's hostname, as the browser reports it
 * @param identitySite - The identity's cookie domain — `orpheusquest.com`
 * @returns Whether this page must hand off to sign in
 */
export const isForeignHostname = (hostname: string, identitySite: string | undefined): boolean => {
	if (identitySite === undefined) {
		return false;
	}

	const host = hostname.toLowerCase();
	const site = identitySite.toLowerCase();

	if (host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host.endsWith('.localhost')) {
		return false;
	}

	return host !== site && !host.endsWith(`.${site}`);
};

/**
 * The address a foreign page sends the player to: the home host, with
 * where to bring them back.
 *
 * @param handoffUrl - The home page that performs handoffs — `VITE_HANDOFF_URL`
 * @param returnUrl - This page, without its fragment
 * @returns The departure address
 */
export const handoffDepartureUrl = (handoffUrl: string, returnUrl: string): string => {
	const departure = new URL(handoffUrl);

	departure.searchParams.set(HANDOFF_RETURN_PARAM, returnUrl);

	return departure.toString();
};

/**
 * The address the home host sends the player back to: the return, with
 * the token in the fragment.
 *
 * @param returnUrl - The address the foreign page asked to return to
 * @param token - The one-time token
 * @returns The return address
 */
export const handoffReturnUrl = (returnUrl: string, token: string): string => {
	const destination = new URL(returnUrl);

	destination.hash = `${HANDOFF_TOKEN_PARAM}=${encodeURIComponent(token)}`;

	return destination.toString();
};

/**
 * The return address a home page was asked to hand off to, when the query
 * carries one that could be a page we serve: `https:` (a creator's domain
 * is never plain), and a whole origin with a path — no fragment, since the
 * fragment is where the token goes. Whether the hostname IS served is the
 * host records' question, asked separately.
 *
 * @param searchString - The page's `location.search`
 * @returns The return address, or nothing
 */
export const handoffRequestOf = (searchString: string): string | undefined => {
	const asked = new URLSearchParams(searchString).get(HANDOFF_RETURN_PARAM);

	if (asked === null) {
		return undefined;
	}

	const url = URL.parse(asked);

	return url !== null && url.protocol === 'https:' && url.hash === '' && url.username === '' && url.password === '' ? url.toString() : undefined;
};

/**
 * The token a foreign page arrived with, when the fragment carries one.
 *
 * @param hashString - The page's `location.hash`
 * @returns The token, or nothing
 */
export const handoffTokenOf = (hashString: string): string | undefined => {
	const token = new URLSearchParams(hashString.replace(/^#/u, '')).get(HANDOFF_TOKEN_PARAM);

	return token === null || token.length === 0 ? undefined : token;
};

/** Where a bearer session is kept: the sliver of `localStorage` used, so a test hands in a map. */
export interface BearerSessionStore {
	clear(): void;
	read(): string | undefined;
	write(token: string): void;
}

/**
 * The bearer session in `localStorage`, every call guarded — storage can
 * be refused, absent, or wiped, and a page must render either way.
 *
 * @param storage - The browser's `localStorage`, or a stand-in
 * @returns The store
 */
export const createBearerSessionStore = (storage: Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>): BearerSessionStore => ({
	clear: () => {
		try {
			storage.removeItem(BEARER_SESSION_STORAGE_KEY);
		} catch {
			// Storage refused: there was nothing kept to clear.
		}
	},
	read: () => {
		try {
			return storage.getItem(BEARER_SESSION_STORAGE_KEY) ?? undefined;
		} catch {
			return undefined;
		}
	},
	write: (token) => {
		try {
			storage.setItem(BEARER_SESSION_STORAGE_KEY, token);
		} catch {
			// Storage refused: the session lasts this page load, and the next visit hands off again.
		}
	},
});

/**
 * A `fetch` that carries the bearer session, when one is held, on every
 * request — the one seam through which the identity, play and store
 * clients all speak for the account from a foreign hostname. A home page
 * holds none and this is the plain `fetch`.
 *
 * @param bearer - The store the session is read from
 * @param fetchImplementation - The fetch to wrap
 * @returns The wrapped fetch
 */
export const withBearerSession =
	(bearer: BearerSessionStore, fetchImplementation: typeof fetch): typeof fetch =>
		async (input, init) => {
			const token = bearer.read();

			if (token === undefined) {
				return fetchImplementation(input, init);
			}

			const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));

			headers.set('Authorization', `Bearer ${token}`);

			return fetchImplementation(input, { ...init, headers });
		};
